/**
 * Dinheiro no front — CONSTITUICAO.md §1.
 *
 * A API transporta INTEIRO em centavos. O front formata para exibir e analisa
 * o que a pessoa digitou. Não faz aritmética decimal: o único cálculo
 * permitido é somar inteiros, e mesmo esse pertence ao backend quando o valor
 * é gravado.
 *
 * `0.1 + 0.2 !== 0.3` em JavaScript. Num PDV isso é o troco errado.
 */

/** Valor monetário em centavos de real. R$ 129,90 → 12990. */
export type Centavos = number

const formatador = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const formatadorMoeda = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

/** Formata sem o símbolo: 12990 → "129,90". */
export function formatar(centavos: Centavos): string {
  return formatador.format(centavos / 100)
}

/** Formata com o símbolo: 12990 → "R$ 129,90". */
export function formatarMoeda(centavos: Centavos): string {
  return formatadorMoeda.format(centavos / 100)
}

/**
 * Converte o texto digitado em centavos, ou devolve null se for inválido.
 *
 * Aceita as formas que aparecem de fato num balcão brasileiro:
 * "1.299,90", "1299,90", "1299.90", "129,9", "R$ 129".
 *
 * A conversão é feita por manipulação de dígitos, e não por `parseFloat`, para
 * não passar por ponto flutuante em momento nenhum: `parseFloat("1299.90") *
 * 100` dá 129989.99999999999.
 */
export function analisar(texto: string): Centavos | null {
  const limpo = texto.trim().replace(/^R\$\s*/i, '').replace(/\s/g, '')
  if (limpo === '') return null

  let negativo = false
  let corpo = limpo
  if (corpo.startsWith('-')) {
    negativo = true
    corpo = corpo.slice(1)
  } else if (corpo.startsWith('+')) {
    corpo = corpo.slice(1)
  }

  const temVirgula = corpo.includes(',')
  const temPonto = corpo.includes('.')

  let inteiro: string
  let decimal: string

  if (temVirgula && temPonto) {
    // "1.299,90" — a vírgula decide, o ponto é milhar. A ordem inversa não é
    // digitada em pt-BR e é recusada de propósito.
    if (corpo.lastIndexOf(',') < corpo.lastIndexOf('.')) return null
    const semMilhar = corpo.replace(/\./g, '')
    ;[inteiro, decimal] = cortarUltimo(semMilhar, ',')
  } else if (temVirgula) {
    ;[inteiro, decimal] = cortarUltimo(corpo, ',')
  } else if (temPonto) {
    const [, apos] = cortarUltimo(corpo, '.')
    // Ponto único com 3 dígitos depois é separador de milhar ("1.299").
    if (apos.length === 3) {
      inteiro = corpo.replace(/\./g, '')
      decimal = ''
    } else {
      ;[inteiro, decimal] = cortarUltimo(corpo, '.')
    }
  } else {
    inteiro = corpo
    decimal = ''
  }

  if (decimal.length > 2) return null
  if (!/^\d*$/.test(inteiro) || !/^\d*$/.test(decimal)) return null
  if (inteiro === '' && decimal === '') return null

  const reais = inteiro === '' ? 0 : Number(inteiro)
  const centavos = Number(decimal.padEnd(2, '0') || '0')

  if (!Number.isSafeInteger(reais) || !Number.isSafeInteger(centavos)) return null

  const total = reais * 100 + centavos
  if (!Number.isSafeInteger(total)) return null

  return negativo ? -total : total
}

function cortarUltimo(texto: string, separador: string): [string, string] {
  const posicao = texto.lastIndexOf(separador)
  if (posicao < 0) return [texto, '']
  return [texto.slice(0, posicao), texto.slice(posicao + 1)]
}

/** Diz se o texto digitado é um valor monetário aceitável. */
export function ehValido(texto: string): boolean {
  return analisar(texto) !== null
}
